import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import BalanceSheetPieChart from './balanceSheetPieChart';
import TwoPieChartsSkeleton from '@/components/common/charts/TwoPieChartsSkeleton';
import CategoryBarChart, {
  type ChartPercentModes,
} from '@/components/charts/CategoryBarChart';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import BalanceSheetGrid from './balanceSheetGrid';
import {
  AccountCategory,
  BalanceDataInterface,
} from '@/interfaces/balanceSheet';
import { calculateFormattedNetWorth } from '@/utils/balance-sheet';
import { mobileWidth } from '@/constants';
import { DataRangeFilterEnum } from '@/constants/dates';
import { ViewMode } from '@/constants/enum';
import ViewModeBar from '@/components/common/ViewModeBar';
import { useLocalization } from '@/hooks/useLocalization';
import BalanceSheetHistoricalGrid from '@/components/grids/historical/BalanceSheetHistoricalGrid';
import HistoricalTrendView from '@/components/grids/historical/HistoricalTrendView';
import HistoricalTrendChart from '@/components/charts/historicalTrend/HistoricalTrendChart';

interface BalanceSheetDetailsProps {
  balanceData: BalanceDataInterface;
  dateFilter: DataRangeFilterEnum;
  startDate?: string;
  endDate?: string;
  isLoading?: boolean;
  firmDefaultView?: ViewMode;
  excludedAccounts?: number[];
  excludedCategories?: number[];
  excludedAccountTypes?: number[];
  excludedInstitutions?: number[];
  onViewModeChange?: (viewMode: ViewMode) => void;
  isWidget?: boolean;
  /** When true, omits the inner paper frame (for embeddable widgets with an outer frame). */
  disablePaperFrame?: boolean;
  /** Advisor Balance Sheet page: the grid pins column totals (IU-64). */
  advisorView?: boolean;
  /**
   * Stacks assets above liabilities, for the half-width widget slot (IU-245).
   * Left undefined, each view keeps the layout it chose before.
   */
  stacked?: boolean;
  /**
   * When provided, a category click bubbles up instead of using the inline
   * drill-down, so the host can render a full-page overlay (IU-31).
   */
  onDrilldown?: (category: AccountCategory) => void;
  /**
   * Controls the view from outside, for a host that unmounts these details
   * while it reloads and needs the view to survive (IU-245). Changes are
   * reported through `onViewModeChange`. Left undefined, the view is kept here.
   */
  viewMode?: ViewMode;
  /** Controls the chart's $/% choice from outside; see `viewMode`. */
  chartPercentModes?: ChartPercentModes;
  onChartPercentModesChange?: (modes: ChartPercentModes) => void;
}

export default function BalanceSheetDetails({
  balanceData,
  dateFilter,
  startDate,
  endDate,
  isLoading = false,
  firmDefaultView,
  excludedAccounts = [],
  excludedCategories = [],
  excludedAccountTypes = [],
  excludedInstitutions = [],
  onViewModeChange,
  isWidget = false,
  disablePaperFrame = false,
  advisorView = false,
  stacked,
  onDrilldown,
  viewMode: controlledViewMode,
  chartPercentModes,
  onChartPercentModesChange,
}: BalanceSheetDetailsProps) {
  // Both the advisor page and the widget supply their own frame, but only the
  // widget wants the tighter header. The advisor page is meant to read like the
  // End User one, so it keeps that page's spacing (IU-346).
  const compactHeader = disablePaperFrame && !advisorView;
  // The advisor page's own frame pads by 4, which would inset this line. The End
  // User page runs it to both edges of the container, so undo that padding. The
  // widget's container pads by 3, so the same negative inset would overhang the
  // card edge instead of meeting it.
  const dividerInset = advisorView && !isWidget ? -4 : 0;
  // The advisor page's frame already supplies the horizontal padding, but not
  // the vertical, and without it the net worth line crowds the divider.
  const headerPadding = () => {
    if (compactHeader) return 0;
    if (advisorView) return '24px 0';
    return '24px';
  };
  // The advisor page defines exactly three views (IU-167); Historical is not one
  // of them and isn't client/group-scoped, so omit it there to avoid showing
  // advisor-wide history under a client/group filter.
  const viewModes = advisorView
    ? [ViewMode.Table, ViewMode.Chart, ViewMode.Summary]
    : [ViewMode.Table, ViewMode.Chart, ViewMode.Summary, ViewMode.Historical];

  // Initialize view mode with firm default if provided, otherwise default to
  // Table — but never a view the toggle doesn't offer (e.g. a firm default of
  // Historical on the advisor page).
  const [ownViewMode, setOwnViewMode] = useState<ViewMode>(
    firmDefaultView && viewModes.includes(firmDefaultView)
      ? firmDefaultView
      : ViewMode.Table,
  );
  const isViewControlled = controlledViewMode !== undefined;
  const viewMode = controlledViewMode ?? ownViewMode;
  const setViewMode = (next: ViewMode) => {
    if (isViewControlled) {
      onViewModeChange?.(next);
      return;
    }
    setOwnViewMode(next);
  };
  const isMobileMatch = useMediaQuery(mobileWidth);
  const { t } = useLocalization();
  const balanceSheetTrendSeries = useMemo(
    () =>
      [
        { trendType: 'deposits', label: t('assets', 'Assets') },
        {
          trendType: 'liabilities',
          label: t('liabilities', 'Liabilities'),
          absolute: true,
        },
      ] as const,
    [t],
  );

  useEffect(() => {
    if (isViewControlled) return;
    onViewModeChange?.(ownViewMode);
  }, [isViewControlled, onViewModeChange, ownViewMode]);

  const getDetailsContainer = () => {
    if (viewMode === ViewMode.Summary) {
      if (isLoading) {
        return <TwoPieChartsSkeleton variant='balanceSheet' />;
      }
      return (
        <BalanceSheetPieChart
          balanceData={balanceData}
          dateFilter={dateFilter}
          stacked={stacked}
          onDrilldown={onDrilldown}
          fillDrilldownHeight={!isWidget}
        />
      );
    }
    if (viewMode === ViewMode.Chart) {
      return (
        <CategoryBarChart
          balanceData={balanceData}
          dateFilter={dateFilter}
          isWidget={isWidget}
          stacked={stacked}
          onDrilldown={onDrilldown}
          fillDrilldownHeight={!isWidget}
          percentModes={chartPercentModes}
          onPercentModesChange={onChartPercentModesChange}
          groupBySubcategory
        />
      );
    }
    if (viewMode === ViewMode.Historical) {
      const historicalGrid = (
        <BalanceSheetHistoricalGrid
          dateFilter={dateFilter}
          startDate={startDate}
          endDate={endDate}
          excludedAccounts={excludedAccounts}
          excludedCategories={excludedCategories}
          excludedAccountTypes={excludedAccountTypes}
          excludedInstitutions={excludedInstitutions}
          isWidget={isWidget}
        />
      );
      if (isWidget) return historicalGrid;
      return (
        <HistoricalTrendView
          table={historicalGrid}
          chart={
            <HistoricalTrendChart
              startDate={startDate}
              endDate={endDate}
              seriesRequests={balanceSheetTrendSeries}
              excludedAccounts={excludedAccounts}
              excludedCategories={excludedCategories}
              excludedAccountTypes={excludedAccountTypes}
              excludedInstitutions={excludedInstitutions}
            />
          }
        />
      );
    }
    return (
      <BalanceSheetGrid
        balanceData={balanceData}
        calculatedTotalAssets={balanceData.assets.total}
        dateFilter={dateFilter}
        isLoading={isLoading}
        advisorView={advisorView}
        stacked={stacked ?? false}
        onDrilldown={onDrilldown}
        fillDrilldownHeight={!isWidget}
      />
    );
  };

  const formattedNetWorth = useMemo(
    () =>
      calculateFormattedNetWorth(
        balanceData.assets.total,
        Math.abs(balanceData.liabilities.total),
      ),
    [balanceData.assets.total, balanceData.liabilities.total],
  );

  const detailsContent = (
    <>
      <Box
        sx={{
          display: 'flex',
          padding: headerPadding(),
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: compactHeader ? 2 : '24px',
          alignSelf: 'stretch',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            alignSelf: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant='h6'
            marginTop={compactHeader ? 0 : 1}
          >
            {t('totalNetWorth')}: {formattedNetWorth}
          </Typography>
          {/* A view switcher is a control, not report content. */}
          <Box sx={{ displayPrint: 'none' }}>
            <ViewModeBar
              viewModes={viewModes}
              currentViewMode={viewMode}
              setViewMode={setViewMode}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        {viewMode !== ViewMode.Historical && (
          <Divider sx={{ mb: 3, mx: dividerInset }} />
        )}
        <Box
          sx={{
            p: disablePaperFrame ? 0 : isMobileMatch ? 2 : 3,
            pt: 0,
          }}
        >
          {getDetailsContainer()}
        </Box>
      </Box>
    </>
  );

  if (disablePaperFrame) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        {detailsContent}
      </Box>
    );
  }

  return (
    <PaperFrameStyled
      sx={{
        flexGrow: 1,
        width: '100% !important',
        maxWidth: '100% !important',
        boxSizing: 'border-box',
      }}
    >
      {detailsContent}
    </PaperFrameStyled>
  );
}
